'use client';

import React, { useState } from 'react';
import { useRouter } from 'next/navigation';
import { Eye, EyeOff, BookOpen, AlertCircle, Loader2, ShieldAlert } from 'lucide-react';

interface LoginFormValues {
  username: string;
  password: string;
}

export default function LoginPageClient() {
  const router = useRouter();
  const [showPassword, setShowPassword] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [loginError, setLoginError] = useState<string | null>(null);
  const [form, setForm] = useState<LoginFormValues>({ username: '', password: '' });
  const [errors, setErrors] = useState<Partial<LoginFormValues>>({});

  const validate = (): boolean => {
    const newErrors: Partial<LoginFormValues> = {};
    if (!form.username.trim()) newErrors.username = 'Username wajib diisi';
    if (!form.password.trim()) newErrors.password = 'Password wajib diisi';
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!validate()) return;

    setIsLoading(true);
    setLoginError(null);

    try {
      const res = await fetch('/api/auth/login', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ username: form.username, password: form.password }),
      });

      const data = await res.json();

      if (!data.success) {
        setLoginError(data.message || 'Username atau password salah.');
        setIsLoading(false);
        return;
      }

      // Check network access
      const netRes = await fetch('/api/network-check', {
        method: 'GET',
      });
      const netData = await netRes.json();

      if (!netData.allowed) {
        setLoginError(`Akses ditolak: ${netData.reason} (IP: ${netData.client_ip})`);
        setIsLoading(false);
        return;
      }

      // Store user data in localStorage
      if (typeof window !== 'undefined') {
        localStorage.setItem('cbt_user', JSON.stringify(data.data));
      }

      router.push('/dashboard');
    } catch {
      setLoginError('Terjadi kesalahan koneksi. Coba lagi.');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-secondary via-background to-blue-50 flex items-center justify-center px-4 py-12">
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        <div className="absolute -top-24 -right-24 w-96 h-96 rounded-full bg-primary opacity-5" />
        <div className="absolute -bottom-24 -left-24 w-80 h-80 rounded-full bg-accent opacity-5" />
      </div>

      <div className="relative w-full max-w-md fade-in">
        {/* Header */}
        <div className="text-center mb-8">
          <div className="inline-flex items-center justify-center w-16 h-16 rounded-2xl bg-primary mb-4 shadow-lg">
            <BookOpen className="w-8 h-8 text-primary-foreground" />
          </div>
          <h1 className="text-2xl font-bold text-foreground tracking-tight">UJIAN ONLINE</h1>
          <p className="text-sm text-muted-foreground mt-1">Sistem CBT Siswa</p>
        </div>

        {/* Login Card */}
        <div className="bg-card rounded-2xl shadow-lg border border-border p-8">
          <div className="mb-6">
            <h2 className="text-xl font-semibold text-foreground">Masuk ke Akun</h2>
            <p className="text-sm text-muted-foreground mt-1">
              Gunakan username dan password yang diberikan sekolah
            </p>
          </div>

          {loginError && (
            <div className="mb-5 flex items-start gap-3 bg-danger-bg border border-danger/20 rounded-xl p-4 fade-in">
              <ShieldAlert className="w-5 h-5 text-danger flex-shrink-0 mt-0.5" />
              <p className="text-sm text-danger leading-relaxed">{loginError}</p>
            </div>
          )}

          <form onSubmit={handleSubmit} noValidate className="space-y-5">
            <div>
              <label htmlFor="username" className="block text-sm font-medium text-foreground mb-1.5">
                Username / NIS
              </label>
              <input
                id="username"
                type="text"
                autoComplete="username"
                placeholder="Masukkan username"
                value={form.username}
                onChange={(e) => setForm((p) => ({ ...p, username: e.target.value }))}
                className={`w-full h-11 px-4 rounded-xl border text-sm bg-input text-foreground placeholder:text-muted-foreground/60 outline-none transition-all duration-150 focus:border-primary focus:ring-2 focus:ring-primary/20 ${
                  errors.username ? 'border-danger ring-2 ring-danger/20' : 'border-border'
                }`}
              />
              {errors.username && (
                <p className="mt-1.5 text-xs text-danger flex items-center gap-1">
                  <AlertCircle className="w-3.5 h-3.5" />
                  {errors.username}
                </p>
              )}
            </div>

            <div>
              <label htmlFor="password" className="block text-sm font-medium text-foreground mb-1.5">
                Password
              </label>
              <div className="relative">
                <input
                  id="password"
                  type={showPassword ? 'text' : 'password'}
                  autoComplete="current-password"
                  placeholder="Masukkan password"
                  value={form.password}
                  onChange={(e) => setForm((p) => ({ ...p, password: e.target.value }))}
                  className={`w-full h-11 px-4 pr-12 rounded-xl border text-sm bg-input text-foreground placeholder:text-muted-foreground/60 outline-none transition-all duration-150 focus:border-primary focus:ring-2 focus:ring-primary/20 ${
                    errors.password ? 'border-danger ring-2 ring-danger/20' : 'border-border'
                  }`}
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 p-1 text-muted-foreground hover:text-foreground transition-colors duration-150"
                  aria-label={showPassword ? 'Sembunyikan password' : 'Tampilkan password'}
                >
                  {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                </button>
              </div>
              {errors.password && (
                <p className="mt-1.5 text-xs text-danger flex items-center gap-1">
                  <AlertCircle className="w-3.5 h-3.5" />
                  {errors.password}
                </p>
              )}
            </div>

            <button
              type="submit"
              disabled={isLoading}
              className="w-full h-11 rounded-xl bg-primary text-primary-foreground text-sm font-semibold transition-all duration-150 hover:bg-primary/90 active:scale-[0.98] disabled:opacity-60 disabled:cursor-not-allowed flex items-center justify-center gap-2 shadow-sm"
            >
              {isLoading ? (
                <>
                  <Loader2 className="w-4 h-4 animate-spin" />
                  <span>Memverifikasi...</span>
                </>
              ) : (
                'Masuk'
              )}
            </button>
          </form>
        </div>

        <p className="text-center text-xs text-muted-foreground mt-6">
          Hubungi admin sekolah jika mengalami masalah login
        </p>
      </div>
    </div>
  );
}